<template>
  <PageChrome :show-header="false">
    <div class="page" v-if="play">
      <SalesPlayHeader
        :play-id="playId"
        :play="play"
        :agent-run-counts="{ running: 0, queued: 0 }"
        :active-stage="stageFilter"
        active-kind="contact"
        :refresh-play="() => refreshAll()"
        @stage-filter="onStageFilter"
      />

      <SalesPlayTabBar :play-id="playId" :engine="play?.engine" />

      <!-- ── Action notice ────────────────────────────────────── -->
      <div v-if="actionNotice" class="actionNotice" role="status">
        <span>{{ actionNotice }}</span>
        <button class="actionNoticeClose" type="button" @click="actionNotice = ''" aria-label="Dismiss">&times;</button>
      </div>

      <!-- ── Toolbar: filters, bulk actions, sort + paging ─────── -->
      <ListToolbar
        noun="candidates"
        :page-size="members.length"
        :selected-count="selectedIds.size"
        :page-fully-selected="allLoadedSelected"
        :total="memberTotal"
        :select-all-max="SELECT_ALL_MAX"
        :selecting-all="selectingAll"
        :range-label="rangeLabel"
        :sort="filters.sort"
        :sort-options="SORT_OPTIONS"
        :can-prev="filters.page > 1"
        :can-next="filters.page < pageCount"
        @toggle-page="toggleSelectAll"
        @select-none="clearSelection"
        @select-all-matching="() => selectAllMatching((msg) => { actionNotice = msg })"
        @prev="goToPage(filters.page - 1)"
        @next="goToPage(filters.page + 1)"
        @update:sort="(v: string) => setFilters({ sort: v })"
      >
        <template #filters>
          <input
            v-model="searchQuery"
            type="text"
            class="searchInput"
            placeholder="Search name, email, or account…"
            @input="onSearchInput"
          />
          <span v-if="stageFilter" class="activeFilterTag">
            {{ STAGE_LABELS[stageFilter] || stageFilter }}
            <button class="activeFilterClear" type="button" @click="onStageFilter(null, 'contact')" aria-label="Clear filter">&times;</button>
          </span>
        </template>

        <template v-if="!isFlock" #actions>
          <button
            v-if="selectionHasStaged"
            class="btn btn-sm primary"
            type="button"
            :disabled="bulkBusy"
            @click="bulkApprove()"
          >Qualify</button>
          <button
            v-if="selectionHasNewQualified"
            class="btn btn-sm primary"
            type="button"
            :disabled="runningAgent"
            @click="bulkRunAgent"
          >Run prospecting</button>
          <button
            v-if="(hasSequenceAction || hasDialerAction) && selectionHasQualifiedContact"
            class="btn btn-sm primary"
            type="button"
            :disabled="bulkBusy"
            @click="bulkRunActions()"
          >Run actions</button>
          <button
            v-if="selectionHasDisqualified"
            class="btn btn-sm primary"
            type="button"
            :disabled="bulkBusy"
            @click="bulkRequalify"
          >Move to qualified</button>
          <button
            v-if="selectionHasNonStaged"
            class="btn btn-sm secondary"
            type="button"
            :disabled="bulkBusy"
            @click="bulkResetToStaged"
          >Reset to staged</button>
          <button
            class="btn btn-sm secondary"
            type="button"
            :disabled="bulkBusy"
            @click="assignOwnerOpen = true"
          >Assign owner</button>
          <button
            class="btn btn-sm danger"
            type="button"
            @click="openExitModal"
          >Exit play…</button>
        </template>

        <template #general-actions>
          <template v-if="!isFlock">
            <AddContactButton icon-only :play-id="playId" :refresh-play="() => refreshAll()" />
            <ImportContactsCsvButton icon-only :play-id="playId" :refresh-play="() => refreshAll()" />
          </template>
          <button class="iconBtn" type="button" title="Download CSV" @click="exportCsv">
            <AppIcon name="download" :size="16" />
          </button>
        </template>
      </ListToolbar>

      <!-- ── Candidate table ──────────────────────────────────── -->
      <CandidateTableShell
        :rows="members"
        :row-key="(m: ActiveMember) => m.membership_id"
        :is-selected="(m: ActiveMember) => isSelected(m) || selectedMembershipId === m.membership_id"
        :is-checked="(m: ActiveMember) => isSelected(m)"
        :loading="membersLoading"
        frozen-column-width="260px"
        :grid-template-columns="gridTemplateColumns"
        @open-row="openDrawerFor"
        @toggle-select="toggleSelect"
      >
        <template #frozen-header>
          <div class="headerCol headerNameCol">Name</div>
        </template>
        <template #frozen-row="{ row: m }">
          <div class="nameCol">
            <CandidateRowIdentity :member="{ kind: 'contact', contact_name: m.contact_name, contact_title: m.contact_title, contact_email: m.contact_email }" />
          </div>
        </template>

        <template #header>
          <div class="headerCol">Account</div>
          <div class="headerCol">Owner</div>
          <div class="headerCol">Added</div>
          <div class="headerCol">Stage</div>
          <div class="headerCol">Next Step</div>
          <div class="headerCol">Links</div>
          <div v-if="hasQualifyBlock" class="headerCol">Qualify</div>
          <div v-if="hasSequenceAction" class="headerCol">Sequence</div>
          <div v-if="hasDialerAction" class="headerCol">Dialer</div>
          <div class="headerCol">Exit Reason</div>
        </template>

        <template #row="{ row: m }">
          <div class="accountCol">
            <CandidateRowIdentity :member="{ kind: 'account', account_domain: m.account_domain, account_name: m.account_name }" />
          </div>

          <div class="ownerColContact">
            <template v-if="editingOwnerId === m.membership_id">
              <select
                class="ownerSelect"
                :value="m.owner_user_id || ''"
                @click.stop
                @change="updateOwner(m, ($event.target as HTMLSelectElement).value)"
                @blur="editingOwnerId = null"
              >
                <option value="">Unassigned</option>
                <option v-for="u in userOptions" :key="u.value" :value="u.value">{{ u.label }}</option>
              </select>
            </template>
            <template v-else>
              <span class="ownerLabel">{{ m.owner?.name || 'Unassigned' }}</span>
              <button
                v-if="!isFlock && (m.state === 'staged' || m.state === 'qualified')"
                class="ownerEditBtn"
                type="button"
                title="Change owner"
                @click.stop="editingOwnerId = m.membership_id"
              >
                <AppIcon name="pencil" :size="12" />
              </button>
            </template>
          </div>

          <!-- ── Added: when this candidate joined the play ────────── -->
          <div class="addedCol">
            <button
              class="addedAtBtn"
              type="button"
              :title="absoluteTime(m.tagged_at)"
              @click.stop="openHistoryModal(m)"
            >
              {{ relativeTime(m.tagged_at) }}
            </button>
          </div>

          <div class="stageCol">
            <button class="n-pill" type="button" title="View status history" @click.stop="openHistoryModal(m)">
              <span class="n-pillDot" :class="`stageDot--${m.state}`" />
              <span>{{ STAGE_LABELS[m.state] || m.state }}</span>
            </button>
          </div>

          <!-- ── Next Step: the single natural action for this row's
               current stage — same handlers the bulk action bar uses,
               scoped to just this one row. ── -->
          <div class="nextStepCol">
            <template v-if="!isFlock">
              <button
                v-if="m.state === 'staged' && !isQualifyRunning(m)"
                class="btn btn-sm primary"
                type="button"
                :disabled="busyMembershipIds.has(m.membership_id)"
                @click.stop="bulkApprove([m])"
              >Run Qualify</button>
              <button
                v-else-if="(hasSequenceAction || hasDialerAction) && m.state === 'qualified'"
                class="btn btn-sm primary"
                type="button"
                :disabled="busyMembershipIds.has(m.membership_id)"
                @click.stop="bulkRunActions([m])"
              >Run Actions</button>
            </template>
          </div>

          <!-- ── Links: same live integration badges as the contact drawer ── -->
          <div class="linkIcons">
            <ContactIntegrationBadges
              :data="{
                linkedin_url: m.contact_linkedin_url,
                salesforce_contact_id: m.salesforce_contact_id,
                salesforce_lead_id: m.salesforce_lead_id,
                hubspot_contact_id: m.hubspot_contact_id,
                pipedrive_person_id: m.pipedrive_person_id,
                leadiq_enrichment_status: m.leadiq_enrichment_status,
              }"
              :size="14"
            />
          </div>

          <!-- ── Qualify: filter-run pill while staged, decision reason once
               resolved — only shown when the play has a filter block. ── -->
          <div v-if="hasQualifyBlock" class="qualifyColOuter">
            <router-link
              v-if="m.qualifying_run_id && (m.qualifying_run_status === 'queued' || m.qualifying_run_status === 'running')"
              :to="`/agent-runs/${m.qualifying_run_id}`"
              @click.stop
            >
              <AgentRunStatusPill :status="m.qualifying_run_status" />
            </router-link>
            <template v-else-if="m.state !== 'staged' && m.qualify_decision_reason">
              <button
                class="qualifyReasonBtn"
                type="button"
                title="View qualify history"
                @click.stop="openHistoryModal(m)"
              >
                {{ reasonLabel(m.qualify_decision_reason) }}
              </button>
              <span v-if="m.qualify_decision_detail" class="qualifyReasonDetail">{{ m.qualify_decision_detail }}</span>
            </template>
          </div>

          <!-- ── Sequence: current step + reachout pills + live run pill ── -->
          <div v-if="hasSequenceAction" class="sequenceCol">
            <router-link
              v-if="m.sequence_running_run"
              :to="`/agent-runs/${m.sequence_running_run.agent_run_id}`"
              @click.stop
            >
              <AgentRunStatusPill :status="m.sequence_running_run.agent_run_status" />
            </router-link>
            <span v-else-if="m.sequence_enrollment" class="actionStatusText" :class="{ actionStatusTextMuted: m.sequence_enrollment.status === 'paused' }">
              Step {{ m.sequence_enrollment.current_step_index + 1 }}/{{ m.sequence_enrollment.total_steps }}
              <template v-if="m.sequence_enrollment.status === 'paused'"> · paused</template>
            </span>
            <div
              v-else-if="m.sequence_needs_attention"
              role="button"
              tabindex="0"
              class="needsAttentionBlock"
              @click.stop="openResolveDialog(m, 'sequence', m.sequence_needs_attention)"
              @keydown.enter.stop="openResolveDialog(m, 'sequence', m.sequence_needs_attention)"
            >
              <span class="n-pill"><span class="n-pillDot n-pillDotDanger" />Needs attention</span>
              <span class="metaSecondary metaSecondaryWrap">{{ m.sequence_needs_attention.reason }}</span>
            </div>
            <span v-else-if="m.sequence_action_status === 'pending'" class="actionStatusText actionStatusTextMuted">Not yet started</span>
            <span v-else-if="m.sequence_action_status === 'not_eligible'" class="actionStatusText actionStatusTextMuted">Not eligible</span>
            <div v-if="(m.sequence_reachouts || []).length > 0" class="memberOutcomes">
              <ReachoutPill v-for="rch in m.sequence_reachouts" :key="rch.id" :reachout="rch" />
            </div>
          </div>

          <!-- ── Dialer: queue status + calls made + live run pill ─── -->
          <div v-if="hasDialerAction" class="dialerCol">
            <router-link
              v-if="m.dialer_running_run"
              :to="`/agent-runs/${m.dialer_running_run.agent_run_id}`"
              @click.stop
            >
              <AgentRunStatusPill :status="m.dialer_running_run.agent_run_status" />
            </router-link>
            <span v-else-if="m.dialer_status?.in_queue" class="actionStatusText">In queue</span>
            <div
              v-else-if="m.dialer_needs_attention"
              role="button"
              tabindex="0"
              class="needsAttentionBlock"
              @click.stop="openResolveDialog(m, 'dialer', m.dialer_needs_attention)"
              @keydown.enter.stop="openResolveDialog(m, 'dialer', m.dialer_needs_attention)"
            >
              <span class="n-pill"><span class="n-pillDot n-pillDotDanger" />Needs attention</span>
              <span class="metaSecondary metaSecondaryWrap">{{ m.dialer_needs_attention.reason }}</span>
            </div>
            <span v-else-if="m.dialer_action_status === 'pending'" class="actionStatusText actionStatusTextMuted">Not yet started</span>
            <span v-else-if="m.dialer_action_status === 'not_eligible'" class="actionStatusText actionStatusTextMuted">Not eligible</span>
            <span v-if="m.dialer_status && m.dialer_status.call_count > 0" class="metaSecondary">
              {{ m.dialer_status.call_count }} call{{ m.dialer_status.call_count !== 1 ? 's' : '' }}
              <template v-if="m.dialer_status.last_disposition"> · {{ m.dialer_status.last_disposition }}</template>
              <template v-if="m.dialer_status.last_call_at"> · {{ relativeTime(m.dialer_status.last_call_at) }}</template>
            </span>
          </div>

          <div class="metaCol">
            <template v-if="TERMINAL_STATES.has(m.state)">
              <span class="metaPrimary">{{ reasonLabel(m.outcome_reason) }}</span>
              <span v-if="m.outcome_detail" class="metaSecondary metaSecondaryWrap">{{ m.outcome_detail }}</span>
            </template>
          </div>
        </template>

        <template #empty>No candidates yet. Add candidates from the overview page or import a CSV.</template>
      </CandidateTableShell>
    </div>

    <div v-else-if="loading" class="loading">Loading…</div>
    <div v-else class="errorBanner">{{ error || 'Play not found.' }}</div>

    <!-- ── ExitPlayModal ────────────────────────────────────── -->
    <ExitPlayModal
      v-if="exitModalIds.length > 0"
      :membership-ids="exitModalIds"
      @close="exitModalIds = []"
      @exited="onBulkExited"
    />

    <!-- ── Needs Attention resolve dialog ──────────────────────── -->
    <NeedsAttentionResolveDialog
      v-if="resolvingActionRunId && resolvingActionId"
      :play-id="playId"
      :action-id="resolvingActionId"
      :action-run-id="resolvingActionRunId"
      :membership-id="resolvingMembershipId"
      :reason="resolvingReason"
      :contact-label="resolvingContactLabel"
      :play-name="play?.name || ''"
      :action-type="resolvingActionType"
      :account-name="resolvingAccountName"
      :account-domain="resolvingAccountDomain"
      :updated-at="resolvingUpdatedAt"
      @close="resolvingActionRunId = null"
      @resolved="onResolved"
    />

    <!-- ── Assign owner modal ────────────────────────────────── -->
    <AssignOwnerModal
      v-if="assignOwnerOpen"
      :selected-count="selectedIds.size"
      :user-options="userOptions"
      :busy="bulkBusy"
      @close="assignOwnerOpen = false"
      @confirm="confirmBulkAssignOwner"
    />

    <!-- ── Reset to staged modal ───────────────────────────────── -->
    <ResetToStagedModal
      v-if="resetStagedIds.length > 0"
      :count="resetStagedIds.length"
      :busy="bulkBusy"
      @close="resetStagedIds = []"
      @confirm="confirmResetToStaged"
    />

    <!-- ── Requalify modal ───────────────────────────────────── -->
    <RequalifyModal
      v-if="requalifyOpen"
      :count="requalifyCount"
      @close="closeRequalifyModal"
      @confirm="confirmRequalify"
    />

    <!-- ── Status history modal ─────────────────────────────── -->
    <MembershipHistoryModal
      v-if="historyModalMembershipId"
      :membership-id="historyModalMembershipId"
      :member="historyModalMember"
      @close="historyModalMembershipId = null"
    />

    <template #footer>
      <ShortcutBar :hotkeys="hotkeys" />
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import ListToolbar from '../components/list/ListToolbar.vue'
import CandidateTableShell from '../components/list/CandidateTableShell.vue'
import ShortcutBar from '../components/ShortcutBar.vue'
import SalesPlayTabBar from '../components/SalesPlayTabBar.vue'
import SalesPlayHeader from '../components/SalesPlayHeader.vue'
import ExitPlayModal from '../components/ExitPlayModal.vue'
import NeedsAttentionResolveDialog from '../components/NeedsAttentionResolveDialog.vue'
import AssignOwnerModal from '../components/AssignOwnerModal.vue'
import ResetToStagedModal from '../components/ResetToStagedModal.vue'
import RequalifyModal from '../components/RequalifyModal.vue'
import AddContactButton from '../components/AddContactButton.vue'
import ImportContactsCsvButton from '../components/ImportContactsCsvButton.vue'
import AppIcon from '../../components/AppIcon.vue'
import AgentRunStatusPill from '../../components/AgentRunStatusPill.vue'
import MembershipHistoryModal from '../components/MembershipHistoryModal.vue'
import CandidateRowIdentity from '../components/CandidateRowIdentity.vue'
import ContactIntegrationBadges from '../../components/ContactIntegrationBadges.vue'
import ReachoutPill, { type ReachoutPillData } from '../components/ReachoutPill.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { SELECT_ALL_MAX, SORT_OPTIONS, useCandidateQuery } from '../composables/useCandidateQuery'
import { useCandidateSelection } from '../composables/useCandidateSelection'
import { useCandidateDrawerNav } from '../composables/useCandidateDrawerNav'
import { STAGE_LABELS, absoluteTime, reasonLabel, relativeTime } from '../utils/candidates'
import { usePlayEngine, type PlayEngine } from '../composables/usePlayEngine'

// ── Types ────────────────────────────────────────────────────────────

interface ActiveMember {
  kind: 'account' | 'contact'
  membership_id: string
  account_id: string | null
  account_domain: string | null
  account_name: string | null
  contact_id?: string | null
  contact_name?: string | null
  contact_email?: string | null
  contact_title?: string | null
  source?: string | null
  channel?: string | null
  state: 'staged' | 'qualified' | 'active' | 'disqualified' | 'engaged' | 'timed_out' | 'deleted'
  outcome_reason?: string | null
  outcome_detail?: string | null
  leadiq_enrichment_status?: string | null
  contact_linkedin_url?: string | null
  salesforce_contact_id?: string | null
  salesforce_lead_id?: string | null
  hubspot_contact_id?: string | null
  pipedrive_person_id?: string | null
  work_phase: string
  tagged_at: string | null
  last_agent_run_at: string | null
  last_agent_run_id?: string | null
  last_agent_run_status?: string | null
  last_agent_run_created_at?: string | null
  sequence_reachouts?: ReachoutPillData[]
  qualifying_run_id?: string | null
  qualifying_run_status?: string | null
  qualify_decision_reason?: string | null
  qualify_decision_detail?: string | null
  implicit_from_contact_add?: boolean
  owner_user_id?: string | null
  owner?: { user_id?: string; email?: string; name?: string } | null
  prospecting_state?: string | null
  prospecting_followup_date?: string | null
  sequence_enrollment?: {
    sequence_name: string | null
    status: string
    current_step_index: number
    total_steps: number
  } | null
  dialer_status?: {
    in_queue: boolean
    call_count: number
    last_call_at: string | null
    last_disposition: string | null
  } | null
  sequence_running_run?: RunningRunInfo | null
  dialer_running_run?: RunningRunInfo | null
  sequence_needs_attention?: NeedsAttentionInfo | null
  dialer_needs_attention?: NeedsAttentionInfo | null
  sequence_action_status?: string | null
  dialer_action_status?: string | null
}

interface RunningRunInfo {
  action_run_status: string
  agent_run_id: string | null
  agent_run_status: string | null
  agent_run_created_at: string | null
}

interface NeedsAttentionInfo {
  reason: string | null
  action_run_id: string
  updated_at: string | null
}

interface PlayDetail {
  id: string
  name: string
  description: string | null
  status: string
  engine?: PlayEngine
  created_by: { id: string; email: string; name: string } | null
  pending_review_count?: number
  qualified_count?: number
  active_count?: number
  engaged_count?: number
  timed_out_count?: number
  disqualified_count?: number
  new_member_count?: number
  pending_reachouts_count?: number
  pending_human_dials_count?: number
  prospecting_agent?: { id: string; approach: string | null; status: string } | null
  config?: { auto_run_prospecting?: string | null }
  icp?: { id: string; current_version: number; config: Record<string, unknown> } | null
  personas?: { local: any[]; referenced: any[] }
  actions?: { id: string; action_type: string }[]
  filters?: any[]
}

// ── Composables & route state ────────────────────────────────────────

const route = useRoute()
const router = useRouter()
const { setScope, clearScope } = useChatScope()

const playId = computed(() => String(route.params.id || ''))

const {
  filters, items: members, total: memberTotal, loading: membersLoading,
  error: membersError, pageCount, rangeLabel, exportCsv,
  load: loadMembers, fetchAllMatching, setFilters, goToPage, idAfter, idBefore,
} = useCandidateQuery({
  routeName: 'sales-play-candidates-people',
  routeParams: () => ({ id: playId.value }),
  playId: () => playId.value,
  play: () => play.value,
  fixedKind: 'contact',
  defaultStage: 'all',
  includeSequenceStatus: () => hasSequenceAction.value,
  includeDialerStatus: () => hasDialerAction.value,
})

/** The header chips and the filter tag read the stage from the URL. */
const stageFilter = computed(() => (filters.value.stage === 'all' ? null : filters.value.stage))

function onStageFilter(stage: string | null, kind: 'account' | 'contact') {
  if (kind === 'account') {
    router.push(`/sales-plays/${playId.value}/candidates/companies${stage ? `?stage=${stage}` : ''}`)
    return
  }
  setFilters({ stage: stage || 'all' })
}

const searchQuery = ref(filters.value.q)
let searchDebounce: number | undefined

function onSearchInput() {
  if (searchDebounce !== undefined) clearTimeout(searchDebounce)
  searchDebounce = window.setTimeout(() => {
    setFilters({ q: searchQuery.value.trim() })
  }, 300)
}

watch(() => filters.value.q, (q) => { if (q !== searchQuery.value.trim()) searchQuery.value = q })

// Filter changes (stage, play, owner, etc.) clear the selection because the
// rows are conceptually different.  Page changes only reload — selections
// survive paging so users can accumulate picks across pages.
// Not `immediate`: the initial fetch happens in onMounted instead, because
// clearSelection() is declared below this point and an immediate watcher would
// run it before its initialization.
watch(
  () => {
    const { page: _, ...rest } = filters.value
    return JSON.stringify(rest)
  },
  () => {
    clearSelection()
    void loadMembers().then(syncDrawerContext)
  },
)
watch(
  () => filters.value.page,
  () => {
    void loadMembers().then(syncDrawerContext)
  },
)

// ── Core data refs ───────────────────────────────────────────────────

const play = ref<PlayDetail | null>(null)
const { isFlock } = usePlayEngine(play)

const loading = ref(true)
const error = computed(() => playError.value || membersError.value)
const playError = ref('')
const bulkBusy = ref(false)
const runningAgent = ref(false)
const actionNotice = ref<string>('')
const busyMembershipIds = ref<Set<string>>(new Set())
const resolvingActionId = ref<string | null>(null)
const resolvingActionRunId = ref<string | null>(null)
const resolvingMembershipId = ref<string | null>(null)
const resolvingReason = ref<string | null>(null)
const resolvingContactLabel = ref<string>('')
const resolvingActionType = ref<string>('')
const resolvingAccountName = ref<string | null>(null)
const resolvingAccountDomain = ref<string | null>(null)
const resolvingUpdatedAt = ref<string | null>(null)
const userOptions = ref<{ value: string; label: string }[]>([])
const editingOwnerId = ref<string | null>(null)
let memberPollHandle: number | undefined

// ── Selection state ──────────────────────────────────────────────────

const {
  selectedIds, selectedRows, selectingAll, isSelected, toggleSelect,
  clearSelection, allLoadedSelected, toggleSelectAll, selectAllMatching,
} = useCandidateSelection<ActiveMember>({
  items: () => members.value,
  fetchAllMatching,
})

// Selection-based computed flags for conditional bulk actions.
// Resolved against selectedRows (not members) so off-page rows are included.
const selectionHasStaged = computed(() =>
  [...selectedRows.value.values()].some(m => m.state === 'staged' && !m.qualifying_run_id)
)
const selectionHasNewQualified = computed(() =>
  hasProspectingAgent.value &&
  [...selectedRows.value.values()].some(m =>
    m.kind === 'account' && (m.state === 'qualified' || m.state === 'active') && (m.work_phase || 'new') === 'new'
  )
)
const selectionHasDisqualified = computed(() =>
  [...selectedRows.value.values()].some(m => m.state === 'disqualified')
)
const selectionHasNonStaged = computed(() =>
  [...selectedRows.value.values()].some(m => m.state !== 'staged')
)
const selectionHasQualifiedContact = computed(() =>
  [...selectedRows.value.values()].some(m => m.kind === 'contact' && m.state === 'qualified')
)

// ── Exit play modal ──────────────────────────────────────────────────

const exitModalIds = ref<string[]>([])

function openExitModal() {
  exitModalIds.value = [...selectedRows.value.keys()]
}

function onBulkExited(_reason: string, counts?: { exited: number; failed: number }) {
  const exited = counts?.exited ?? 0
  const failed = counts?.failed ?? 0
  actionNotice.value = failed > 0
    ? `Exited ${exited} candidate(s) from play. ${failed} skipped.`
    : `Exited ${exited} candidate(s) from play.`
  clearSelection()
  refreshAll()
}

// ── Bulk assign owner ────────────────────────────────────────────────

const assignOwnerOpen = ref(false)

async function confirmBulkAssignOwner(userId: string) {
  if (!userId || bulkBusy.value) return
  bulkBusy.value = true
  try {
    const resp: any = await apiFetch('/sales-plays/memberships/bulk-assign-owner', {
      method: 'PUT',
      body: {
        membership_ids: [...selectedRows.value.keys()],
        owner_user_id: userId,
      },
    })
    const skippedCount = resp?.skipped?.length || 0
    const assignedCount = resp?.assigned?.length || 0
    actionNotice.value = skippedCount > 0
      ? `Assigned owner to ${assignedCount} candidate(s). ${skippedCount} skipped (enrolled in sequence).`
      : `Assigned owner to ${assignedCount} candidate(s).`
    assignOwnerOpen.value = false
    clearSelection()
    await refreshAll()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Failed to assign owner'
  } finally {
    bulkBusy.value = false
  }
}

// ── Play meta computeds ──────────────────────────────────────────────

// A play with no filter block auto-qualifies every staged candidate
// instantly (see auto_qualify_if_no_filters) -- there is never anything to
// show in this column for such a play, so it's hidden entirely rather than
// always-empty.
const hasQualifyBlock = computed(() => (play.value?.filters?.length || 0) > 0)
const hasProspectingAgent = computed(() => Boolean(play.value?.prospecting_agent))
const hasSequenceAction = computed(() => (play.value?.actions || []).some(a => a.action_type === 'sequence'))
const hasDialerAction = computed(() => (play.value?.actions || []).some(a => a.action_type === 'dialer'))
const newQualifiedCount = computed(() => play.value?.new_member_count || 0)

const statusDotClass = computed(() => {
  const s = String(play.value?.status || '').toLowerCase()
  if (s === 'active') return 'statusDotActive'
  return 'statusDotDisabled'
})

const pauseButtonLabel = computed(() => {
  const s = play.value?.status
  if (s === 'archived') return 'Archived'
  if (s === 'paused') return 'Resume play'
  return 'Pause play'
})

/** Checkbox + Name columns are the shell's frozen pane — omit them here. */
const gridTemplateColumns = computed(() => {
  const base = '220px 140px 90px 110px 150px 80px'
  const qualify = hasQualifyBlock.value ? ' 180px' : ''
  const sequence = hasSequenceAction.value ? ' 180px' : ''
  const dialer = hasDialerAction.value ? ' 180px' : ''
  return `${base}${qualify}${sequence}${dialer} 220px`
})

// ── Users ────────────────────────────────────────────────────────────

async function loadUsers() {
  try {
    const resp = await apiFetch('/settings/team_members')
    const members = Array.isArray(resp?.members) ? resp.members : []
    userOptions.value = members.map((m: any) => ({ value: m.id, label: m.name || m.email }))
  } catch { /* ignore */ }
}

async function updateOwner(member: any, userId: string) {
  const kind = member.kind === 'account' ? 'account' : 'contact'
  try {
    await apiFetch(`/sales-plays/memberships/${member.membership_id}/owner`, {
      method: 'PUT',
      body: { owner_user_id: userId || null, kind },
    })
    member.owner_user_id = userId || null
    const matched = userOptions.value.find(u => u.value === userId)
    member.owner = matched ? { name: matched.label } : null
  } catch (e) {
    console.error('Failed to update owner', e)
  }
  editingOwnerId.value = null
}

// ── Dismiss / Requalify modals ───────────────────────────────────────

const requalifyIds = ref<string[]>([])

function openBulkRequalifyModal() {
  const ids = [...selectedRows.value.values()]
    .filter(m => m.state === 'disqualified')
    .map(m => m.membership_id)
  if (ids.length === 0) return
  requalifyIds.value = ids
}

function closeRequalifyModal() {
  requalifyIds.value = []
}

const requalifyCount = computed(() => requalifyIds.value.length)

const requalifyOpen = computed(() => requalifyIds.value.length > 0)

async function confirmRequalify(reasoning: string | null) {
  if (requalifyIds.value.length === 0) return
  const ids = requalifyIds.value
  closeRequalifyModal()
  bulkBusy.value = true
  try {
    const resp: any = await apiFetch('/sales-plays/memberships/qualify', {
      method: 'POST',
      body: { membership_ids: ids, reasoning },
    })
    const approved = resp?.approved?.length || 0
    const failed = resp?.failed?.length || 0
    actionNotice.value = failed > 0
      ? `Requalified ${approved} candidate(s). ${failed} skipped.`
      : `Requalified ${approved} candidate(s).`
    clearSelection()
    await refreshAll()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Requalify failed'
  } finally { bulkBusy.value = false }
}

// ── Row helpers ──────────────────────────────────────────────────────

const historyModalMembershipId = ref<string | null>(null)
const historyModalMember = ref<{
  kind: 'contact'
  account_domain: string | null
  account_name: string | null
  contact_name: string | null
  contact_title: string | null
} | null>(null)

function isQualifyRunning(m: ActiveMember): boolean {
  return Boolean(m.qualifying_run_id) && (m.qualifying_run_status === 'queued' || m.qualifying_run_status === 'running')
}

function openHistoryModal(m: ActiveMember) {
  historyModalMembershipId.value = m.membership_id
  historyModalMember.value = {
    kind: 'contact',
    account_domain: m.account_domain ?? null,
    account_name: m.account_name ?? null,
    contact_name: m.contact_name ?? null,
    contact_title: m.contact_title ?? null,
  }
}

// ── Drawer integration ───────────────────────────────────────────────

const {
  selectedMembershipId, openDrawerFor, syncDrawerContext, clearDrawerIfOwned, hotkeys,
} = useCandidateDrawerNav<ActiveMember>({
  items: () => members.value,
  playIdFor: () => playId.value,
  idAfter,
  idBefore,
  toggleSelect,
  toggleSelectAll,
})

// ── Actions ──────────────────────────────────────────────────────────

async function bulkApprove(rows?: ActiveMember[]) {
  const isRowLevel = rows !== undefined
  const ids = (rows ?? [...selectedRows.value.values()])
    .filter(m => m.state === 'staged')
    .map(m => m.membership_id)
  if (ids.length === 0) return
  if (isRowLevel) ids.forEach(id => busyMembershipIds.value.add(id))
  else bulkBusy.value = true
  try {
    const resp: any = await apiFetch('/sales-plays/memberships/qualify', {
      method: 'POST',
      body: { membership_ids: ids },
    })
    if (resp?.action === 'filter_run_enqueued') {
      const idSet = new Set(ids)
      const runId = resp.run_id || null
      for (const m of members.value) {
        if (idSet.has(m.membership_id)) {
          m.qualifying_run_id = runId
        }
      }
      if (!isRowLevel) actionNotice.value = `Qualifying ${ids.length} candidate(s) — filters are running.`
    } else {
      const approved = resp?.approved?.length || 0
      const failed = resp?.failed?.length || 0
      if (!isRowLevel || failed > 0) {
        actionNotice.value = failed > 0
          ? `Qualified ${approved} candidate(s). ${failed} skipped.`
          : `Qualified ${approved} candidate(s).`
      }
    }
    clearSelection()
    await refreshAll()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Qualify failed'
  } finally {
    if (isRowLevel) ids.forEach(id => busyMembershipIds.value.delete(id))
    else bulkBusy.value = false
  }
}

function openResolveDialog(m: ActiveMember, actionType: 'sequence' | 'dialer', info: NeedsAttentionInfo) {
  const action = (play.value?.actions || []).find(a => a.action_type === actionType)
  if (!action) return
  resolvingActionId.value = action.id
  resolvingActionRunId.value = info.action_run_id
  resolvingMembershipId.value = m.membership_id
  resolvingReason.value = info.reason
  resolvingContactLabel.value = m.contact_name || m.contact_email || 'Contact'
  resolvingActionType.value = actionType
  resolvingAccountName.value = m.account_name
  resolvingAccountDomain.value = m.account_domain
  resolvingUpdatedAt.value = info.updated_at
}

async function onResolved() {
  resolvingActionRunId.value = null
  await refreshAll()
}

async function bulkRunAgent() {
  const ids = [...selectedRows.value.values()]
    .filter(m => (m.state === 'qualified' || m.state === 'active') && (m.work_phase || 'new') === 'new')
    .map(m => m.membership_id)
  if (ids.length === 0) return
  runningAgent.value = true
  actionNotice.value = ''
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}/run-agent`, {
      method: 'POST',
      body: { membership_ids: ids },
    })
    actionNotice.value = formatRunAgentSummary(resp)
    clearSelection()
    await refreshAll()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Run agent failed'
  } finally { runningAgent.value = false }
}

async function bulkRunActions(rows?: ActiveMember[]) {
  const isRowLevel = rows !== undefined
  const ids = (rows ?? [...selectedRows.value.values()])
    .filter(m => m.kind === 'contact' && m.state === 'qualified')
    .map(m => m.membership_id)
  if (ids.length === 0) return
  if (isRowLevel) ids.forEach(id => busyMembershipIds.value.add(id))
  else bulkBusy.value = true
  actionNotice.value = ''
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}/actions/run-for-memberships`, {
      method: 'POST',
      body: { membership_ids: ids },
    })
    const seeded = Number(resp?.seeded ?? 0)
    if (!isRowLevel || seeded === 0) {
      actionNotice.value = seeded === 0
        ? 'No eligible actions to run.'
        : `Seeded ${seeded} action(s).`
    }
    clearSelection()
    await refreshAll()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Run actions failed'
  } finally {
    if (isRowLevel) ids.forEach(id => busyMembershipIds.value.delete(id))
    else bulkBusy.value = false
  }
}

function bulkRequalify() {
  openBulkRequalifyModal()
}

const resetStagedIds = ref<string[]>([])

function bulkResetToStaged() {
  const ids = [...selectedRows.value.keys()]
  if (ids.length === 0) return
  resetStagedIds.value = ids
}

async function confirmResetToStaged(notes: string | null) {
  if (resetStagedIds.value.length === 0) return
  bulkBusy.value = true
  try {
    const resp: any = await apiFetch('/sales-plays/memberships/reset-to-staged', {
      method: 'POST',
      body: {
        membership_ids: resetStagedIds.value,
        reason: notes,
      },
    })
    const resetCount = resp?.reset?.length || 0
    const failedCount = resp?.failed?.length || 0
    actionNotice.value = failedCount > 0
      ? `Reset ${resetCount} candidate(s) to staged. ${failedCount} skipped.`
      : `Reset ${resetCount} candidate(s) to staged.`
    resetStagedIds.value = []
    clearSelection()
    await refreshAll()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Failed to reset candidates'
  } finally {
    bulkBusy.value = false
  }
}


const TERMINAL_STATES = new Set(['disqualified', 'engaged', 'timed_out', 'finished'])

const SKIP_REASON_LABELS: Record<string, string> = {
  skipped_no_prospecting_agent: 'no prospecting agent configured',
  skipped_auto_off: 'prospecting is turned off for this play',
  skipped_contact_limit_reached: 'already has the max contacts for this account',
}

function formatRunAgentSummary(resp: any): string {
  const enqueued = Number(resp?.enqueued_count ?? 0)
  const attached = Number(resp?.attached_count ?? 0)
  const examined = Number(resp?.examined ?? 0)
  const skipped: { membership_id: string; reason: string }[] = resp?.skipped || []

  const parts: string[] = []
  if (enqueued > 0) parts.push(`${enqueued} new run(s) enqueued`)
  if (attached > 0) parts.push(`${attached} attached to existing run(s)`)

  if (skipped.length > 0) {
    // Group by reason so "4 skipped: already has the max contacts..." reads as
    // one clear sentence instead of one line per membership.
    const countByReason = new Map<string, number>()
    for (const s of skipped) {
      countByReason.set(s.reason, (countByReason.get(s.reason) || 0) + 1)
    }
    for (const [reason, count] of countByReason) {
      const label = SKIP_REASON_LABELS[reason] || reason.replace(/^skipped_/, '').replace(/_/g, ' ')
      parts.push(`${count} skipped (${label})`)
    }
  }

  if (parts.length === 0) {
    return examined === 0 ? 'No eligible members to run.' : `Examined ${examined} member(s); nothing newly enqueued.`
  }
  return parts.join(', ')
}

// ── Data fetching ───────────────────────────────────────────────────

let pollHandle: number | undefined

async function fetchPlayMeta(silent = false) {
  if (!silent) { loading.value = true; playError.value = '' }
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}`)
    play.value = resp?.play || null
    if (silent) playError.value = ''
  } catch (e: any) {
    if (!silent) playError.value = e?.message || 'Failed to load'
  } finally {
    if (!silent) loading.value = false
  }
}

async function fetchMembers() {
  await loadMembers()
  syncDrawerContext()
}

async function refreshAll() {
  await Promise.all([fetchPlayMeta(true), fetchMembers()])
}

function startMemberPolling() {
  stopMemberPolling()
  memberPollHandle = window.setInterval(() => {
    if (!document.hidden) {
      pollMembers()
    }
  }, 5000)
}

/**
 * Refresh the current page in place. `forPoll` merges by identity rather than
 * replacing the array, so the open drawer and the checkbox selection survive a
 * tick that changed nothing.
 */
async function pollMembers() {
  if (members.value.length === 0) return
  await loadMembers({ forPoll: true })
  syncDrawerContext()
}

function stopMemberPolling() {
  if (memberPollHandle !== undefined) {
    window.clearInterval(memberPollHandle)
    memberPollHandle = undefined
  }
}

// ── Lifecycle ────────────────────────────────────────────────────────

onMounted(async () => {
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value },
  })
  void fetchMembers()
  await fetchPlayMeta(false)
  // fetchMembers() above raced ahead of this and so read hasSequenceAction/
  // hasDialerAction as false (play wasn't loaded yet) -- re-fetch now that
  // they're known, or a play with either action would show empty Sequence/
  // Dialer columns until the next poll tick.
  if (hasSequenceAction.value || hasDialerAction.value) void fetchMembers()
  loadUsers()
  pollHandle = window.setInterval(() => {
    fetchPlayMeta(true)
  }, 2000)
  startMemberPolling()
})

onBeforeUnmount(() => {
  if (pollHandle !== undefined) window.clearInterval(pollHandle)
  stopMemberPolling()
  if (searchDebounce !== undefined) clearTimeout(searchDebounce)
  clearScope()
  clearDrawerIfOwned()
})
</script>

<style scoped>
.searchInput {
  width: 240px;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  background: var(--color-bg-surface);
  transition: border-color var(--duration-fast) var(--ease-out);
}
.searchInput::placeholder { color: var(--color-text-tertiary); }
.searchInput:focus { outline: none; border-color: var(--color-accent); }

.activeFilterTag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2) 2px var(--space-3);
  background: var(--color-surface-subtle, #f3f4f6);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
}
.activeFilterClear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  color: var(--color-text-secondary);
  border-radius: var(--radius-full);
}
.activeFilterClear:hover { background: var(--color-text-secondary); color: white; }

/* ── Column header ────────────────────────────────────────── */
.headerCol { min-width: 0; }

.stageCol { display: flex; align-items: center; gap: 6px; min-width: 0; }
.stageDot--staged { background: var(--color-warning, #c2410c); }
.stageDot--qualified { background: var(--color-accent); }
.stageDot--active { background: var(--color-info, #1d4ed8); }
.stageDot--engaged { background: var(--color-success, #047857); }
.stageDot--timed_out { background: var(--color-text-tertiary); }
.stageDot--disqualified { background: var(--color-danger, #b91c1c); }
.stageDot--deleted { background: var(--color-danger, #b91c1c); }

.nextStepCol {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}
.nextStepCol .btn {
  white-space: nowrap;
}

.nameCol {
  display: flex;
  align-items: center;
  width: 260px;
  min-width: 0;
}

.accountCol {
  display: flex;
  align-items: center;
  min-width: 0;
}

.linkIcons {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

/* ── Added column: tagged_at, clickable for status history ────── */
.addedCol {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}
.addedAtBtn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.addedAtBtn:hover { color: var(--color-accent); }

/* ── Meta / owner / status columns ────────────────────────── */
.metaCol {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}
.metaPrimary {
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.metaSecondary {
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.metaSecondaryWrap {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.ownerColContact {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}
.ownerLabel {
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ownerEditBtn {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--color-text-secondary);
  opacity: 0;
  transition: opacity 0.1s;
}
.ownerColContact:hover .ownerEditBtn { opacity: 0.6; }
.ownerEditBtn:hover { opacity: 1 !important; }
.ownerSelect {
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--fg);
  width: 100%;
}

/* ── Qualify column: filter-run pill, only shown when the play has one ── */
.qualifyColOuter {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}
.qualifyReasonBtn {
  display: block;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.qualifyReasonBtn:hover { color: var(--color-accent); }
.qualifyReasonDetail {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ── Sequence / Dialer columns: one per action node the play defines ──── */
.sequenceCol, .dialerCol {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  min-width: 0;
  overflow: hidden;
}
.actionStatusText {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.actionStatusTextMuted { color: var(--color-text-tertiary); }
.n-pillDotDanger {
  background: var(--color-danger);
}
.needsAttentionBlock {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  cursor: pointer;
}

.memberOutcomes { display: flex; flex-wrap: wrap; gap: var(--space-1); }

/* ── Action notice ────────────────────────────────────────── */
.actionNotice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin: 0 0 var(--space-4);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle, #f3f4f6);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.actionNoticeClose {
  background: transparent;
  border: 0;
  font-size: var(--fs-lg);
  line-height: 1;
  cursor: pointer;
  color: var(--color-text-tertiary);
}
.actionNoticeClose:hover { color: var(--color-text-primary); }

/* ── Empty / loading ──────────────────────────────────────── */
.loading, .errorBanner {
  margin-top: var(--space-7);
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  color: var(--color-text-secondary);
}

.disabled { opacity: 0.5; cursor: not-allowed; }
</style>
